import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Heart, Ban, Lock, Flag, Check, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useEdition } from "@/lib/edition";

const RULES = [
  [Heart, "Be kind and be good", "Treat everyone with respect, in chats, posts, notes and messages."],
  [Ban, "No abuse or bullying", "No insults, threats, hate, harassment or bad language. Messages like this are blocked."],
  [ShieldCheck, "Keep it appropriate", "Only share school-friendly content. No sexual content, drugs or violence. Don't share phone numbers, addresses or social media usernames, yours or anyone else's."],
  [Flag, "Report problems", "If something feels wrong, tap Report. Admins review every report and can remove content or ban accounts."],
  [Lock, "Your data is yours", "We will never sell or steal your data. It's only used to run your Daybreyk, and you can ask us to delete it any time."],
];
const CORP_RULES = [
  [Heart, "Be respectful and professional", "Treat colleagues with respect in chats, posts, notes and messages."],
  [Ban, "No harassment or discrimination", "No insults, threats, hate, bullying or abusive language. Messages like this are blocked."],
  [ShieldCheck, "Keep it safe for work", "No sexual content, illegal drugs or violence. Only share confidential company information where you're allowed to."],
  [Flag, "Report problems", "If something feels wrong, tap Report. Admins review every report and can remove content or ban accounts."],
  [Lock, "Your data is yours", "We will never sell your data. It's only used to run your Daybreyk, and you can ask us to delete it any time."],
];

export const ConductRules = () => {
  const { corp } = useEdition();
  return (
  <ul className="space-y-3">
    {(corp ? CORP_RULES : RULES).map(([I, t, d]) => (
      <li key={t} className="flex gap-3 rounded-2xl border border-slate-200 bg-surface-2 p-4">
        <span className="grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-gold-400/15 text-gold-300"><I className="w-5 h-5" /></span>
        <span><span className="block font-semibold text-slate-900">{t}</span><span className="block text-sm text-slate-600">{d}</span></span>
      </li>
    ))}
  </ul>
  );
};

export default function Conduct() {
  const { setUser, logout } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const { w } = useEdition();
  const agree = async () => {
    setBusy("yes");
    try { const { data } = await api.post("/conduct/accept"); const me = await api.get("/auth/me"); setUser(me.data); nav(me.data?.onboarded === false ? "/welcome" : "/"); return data; }
    catch { setBusy(""); }
    return null;
  };
  const decline = async () => { setBusy("no"); await logout(); nav("/login", { replace: true }); };
  return (
    <main data-testid="conduct-page" className="mx-auto max-w-2xl px-4 sm:px-6 py-10 sm:py-14 space-y-6">
      <div>
        <p className="eyebrow">Before you start</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl text-slate-900 leading-none">{w("Our code of conduct", "Workplace code of conduct")}</h1>
        <p className="mt-3 text-slate-600">{w("Daybreyk is a place for students to help each other.", "Daybreyk Corporate is a professional space for you and your team.")} To use it, please agree to these rules.</p>
      </div>
      <ConductRules />
      <div className="flex flex-wrap gap-3">
        <button data-testid="conduct-agree" onClick={agree} disabled={Boolean(busy)} className="inline-flex items-center gap-2 rounded-full bg-gold-400 px-6 py-3 font-bold text-black hover:bg-gold-300 disabled:opacity-50">{busy === "yes" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}I agree</button>
        <button data-testid="conduct-decline" onClick={decline} disabled={Boolean(busy)} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 font-semibold text-slate-700 hover:border-rose-600">{busy === "no" ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}I don't agree</button>
      </div>
      <p className="text-xs text-slate-500">If you don't agree, you'll be signed out and taken back to the login page.</p>
    </main>
  );
}
